import React, { useState, useEffect } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { DateTime } from 'luxon';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CardHeader from '@material-ui/core/CardHeader';
import Avatar from '@material-ui/core/Avatar';

import CloseIcon from '@material-ui/icons/Close';
import LeftIcon from '@material-ui/icons/ArrowBackIos';

import SearchUsersAutocomplete from 'app/components/user/UserSearchAutocomplete';
import RedButton from 'app/components/buttons/RedButton';
import Loading from 'app/components/loaders/Loading';
import Field from 'app/components/form/Field';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';
import DefaultFieldValue from 'app/utils/DefaultFieldValue';
import ChargeForm from 'app/components/accounting/payments/ChargeForm';

const useStyles = makeStyles(theme => ({
    
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogActions: {
        justifyContent: 'space-between'
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    }
}))

export default function EditEntryDialog(props) {

    // Construct -----------------------------

    const {
        form,
        id,
        open,
        onClose,
        onSave,
        onClosed,
        admin,
        hash
    } = props

    const api = useApi();
    const { app } = useApp();
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ values, setValues ] = useState({});
    const [ user, setUser ] = useState({ id: 0 });
    const [ urlPrefix, setUrlPrefix ] = useState(`/form/${form.id}/entry`)

    const [ errors, setErrors ] = useState({});
    const [ loading, setLoading ] = useState(false);
    const [ step, setStep ] = useState(1);

    const [ clientSecret, setClientSecret ] = useState(null);
    const [ paymentIntentId, setPaymentIntentId ] = useState(null);
    const [ stripePromise, setStripePromise ] = useState(null);
    const [ paymentAmount, setPaymentAmount ] = useState(0);
    const [ paymentItem, setPaymentItem ] = useState("");

    const getEntry = () => {
        setLoading(true);

        api.get(`${urlPrefix}/${id}`).then(res => {

                mapValues(form.fields, res.entry.values);

                if (res.entry.user) setUser(res.entry.user);

            }).finally(() => setLoading(false));
    }

    const mapValues = (fields, values, newFiles) => {

        const newValues = {};

        // Map the values to their fields
        fields.forEach(f => {
            const v = values ? values.find(val => (val.field_id === f.id)) : false;

            if (v) {
                console.log(v);
                newValues[f.id] = v.value;

                if (f.options.type === 'userselect') {
                    newValues[f.id].users = v.users;
                }
                if (f.options.type === 'upload') {
                    newValues[f.id].file = v.file;
                }
            } else {
                const dv = DefaultFieldValue(f);
                if (dv) newValues[f.id] = dv;
            }
        });

        setValues(newValues);
    }

    const setStripe = () => {
        if (app.club && Array.isArray(app.club.meta.payment_methods) && app.club.meta.payment_methods.includes('stripe') && app.club.meta.stripe_account_id) {
            const stripeOptions = {stripeAccount: app.club.meta.stripe_account_id};
            setStripePromise(loadStripe(process.env.MIX_STRIPE_KEY, stripeOptions));

        } else {
            setStripePromise(null);
        }
    }

    useEffect(() => {
        mapValues(form.fields, null);
        setStripe();

        if (admin) {
            setUrlPrefix(`/admin/form/${form.id}/entry`)
        }
        if (hash) {
            setUrlPrefix(`/form/${hash}`)
        }
    }, []);

    const onOpen = () => {
        if (id > 0) {
            getEntry();
        } else {
            mapValues(form.fields, null);

            if (! admin && app.user) setUser(app.user);
        }

        setErrors({});
        setStep(1);
    }

    // Handlers -------------------------------------------------

    const handleChange = (index , event) => {

        let value;

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        };

        if (errors?.values?.[index]) delete errors.values[index];

        setValues({ ...values, [index]: { value: value} });
    };

    const handleUser = user => {

        if (isError('user')) delete errors.user;

        setUser(user);
    }

    const openPaymentForm = (paymentAmount, paymentItem) => {
        setLoading(true);
        setPaymentAmount(paymentAmount);
        setPaymentItem(paymentItem);

        const intentData = {
            club_id: app.club.id,
            amount: paymentAmount
        }

        api.post('/stripe/form/intent', intentData)
            .then(res => {
                setClientSecret(res.client_secret);
                setPaymentIntentId(res.payment_intent_id);
                setStep(3);
            })
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    }

    // Posters ---------------------------------

    const postEntry = () => {

        setLoading(true);

        const data = { values: values }

        if (user && user.id > 0) {
            data.user_id = user.id;
        }

        const url = hash ? urlPrefix : `${urlPrefix}/${id}`;

        api.post(url, data)
            .then(() => onSave())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    console.log(error?.response?.data?.errors)
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false))
    }

    const deleteEntry = () => {

        setLoading(true);

        api.delete(`${urlPrefix}/${id}`)
            .then(() => onSave())
            .finally(() => setLoading(false))
    }

    const paymentSucceeded = () => {
        // Create an invoice and save server side
        setLoading(true);

        const paymentField = form.fields.find(f => f.options.type === 'paymentform');
        const emailField = form.fields.find(f => f.options.label === paymentField.options?.email_field);
        const defaultUserId = paymentField['options']['payment_user']

        const paymentData = {
            user_id: (user?.id && user.id > 0) ? user.id : defaultUserId,
            club_id: app.club.id,
            form_id: form.id,
            field_id: paymentField.id,
            payment_intent_id: paymentIntentId,
            invoice_item_name: paymentItem,
            invoice_email: values[emailField.id]?.value ?? null
        }

        api.post('/stripe/form/payment/succeeded', paymentData)
            .then(res => {
                setPaymentAmount(0);
                setPaymentItem("");
                setValues({ ...values, [paymentField.id]: { value: res.invoice_number} });
                setStep(1);
                document.getElementById('payment-form-ref').scrollIntoView({ behavior: 'instant', block: 'center' });
            })
            .catch(error => {
                setLoading(false)

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
        // Update button value with relavent payment data from server
        // Move user to tab 1 on payment button
    }

    // Error Handling

    const isError = field => {
        if (errors[field]) return true;
        return false;
    };

    const getErrorMessage = field => {
        if (isError(field)) return errors[field]['message'];
        return "";
    };

    const onExited = () => {
        setValues([]);
        setUser({id: 0});
        setErrors({});
        setLoading(false);
        setStep(1);
        if (onClosed) onClosed();
    }

    // Constants --------------------------

    const initials = user => user.first.charAt(0) + user.last.charAt(0);

    return (
        <Dialog
            open={open}
            onClose={(onClose) ? onClose : null}
            fullWidth
            fullScreen={isMobile}
            onEnter={onOpen}
            onExited={onExited}
            maxWidth="sm"
            aria-labelledby="edit-entry-dialog-title"
            disableBackdropClick
            disableEscapeKeyDown
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-entry-dialog-title">
                {(step === 1 && id > 0) && 'Edit Entry'}
                {(step === 1 && id == 0) && 'New Entry'}
                {(step === 2) && 'Delete Entry'}
                {(step === 3) && 'Submit Payment'}
                {(onClose) &&
                    <IconButton
                        aria-label="Cancel"
                        className={classes.closeButton}
                        onClick={onClose}
                        disabled={loading}
                        >
                        <CloseIcon />
                    </IconButton>
                }
            </DialogTitle>
            <DialogContent dividers>
                {(step === 1) &&
                    <Grid container spacing={3} className={classes.marginBottom}>
                        {form.fields.map(field => (
                            <Field
                                key={field.id}
                                field={field}
                                fields={form.fields}
                                value={values[field.id]}
                                values={values}
                                handleChange={handleChange}
                                error={isError(field.id)}
                                errorMessage={getErrorMessage(field.id)}
                                admin={admin}
                                openPaymentForm={openPaymentForm}
                            />
                        ))}
                        {admin &&
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Entry Owner
                                    </Typography>
                                    <Typography variant="body1">
                                        Select a user who can view and edit user editable fields for this entry.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    {(user.id > 0) ? (
                                        <CardHeader
                                            avatar={
                                                <Avatar
                                                    aria-label={user.full_name}
                                                    alt={user.full_name}
                                                    className={classes.avatar}
                                                    src={user.avatar ? user.avatar.full_url : null}
                                                    >
                                                    {initials(user)}
                                                </Avatar>
                                            }
                                            title={user.full_name}
                                            subheader={user.membership_title}
                                            action={
                                                <IconButton aria-label="Remove User" onClick={() => handleUser({id:0})}>
                                                    <CloseIcon />
                                                </IconButton>
                                            }
                                        />
                                    ) : (
                                        <SearchUsersAutocomplete
                                            label="Search Users"
                                            closeOnSelect
                                            setOnSelect
                                            onSelect={handleUser}
                                        />
                                    )}
                                    { isError('user') &&
                                        <Typography variant="caption" color="error">
                                            {getErrorMessage('user')}
                                        </Typography>
                                    }
                                </Grid>
                            </>
                        }
                    </Grid>
                }
                {(step === 2) &&
                    <Typography variant="subtitle1" color="error" gutterBottom>
                        Are you sure you want to delete this entry?
                    </Typography>
                }
                {(step === 3 && clientSecret) &&
                    <Elements options={{clientSecret}} stripe={stripePromise}>
                        <DialogContentText gutterBottom>
                            Select a payment method below.
                        </DialogContentText>
                        <ChargeForm 
                            paymentItem={paymentItem}
                            paymentAmount={paymentAmount}
                            paymentSucceeded={paymentSucceeded}
                            loading={loading} 
                            setLoading={setLoading}
                        />
                    </Elements>
                }
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                {(step === 1) &&
                    <>
                        <RedButton
                            onClick={() => setStep(2)}
                            disabled={loading || id == 0}
                            >
                            Delete
                        </RedButton>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={postEntry}
                            disabled={loading}
                            >
                            {(id > 0) ? 'Update Entry' : 'Submit Entry'}
                        </Button>
                    </>
                }
                {(step === 2) &&
                    <>
                        <Button
                            startIcon={<LeftIcon/>}
                            aria-label="Back"
                            onClick={() => setStep(1)}
                            >
                            Back to Entry
                        </Button>
                        <RedButton
                            onClick={deleteEntry}
                            disabled={loading}
                            variant="contained"
                            >
                            Delete
                        </RedButton>
                    </>
                }
                {(step === 3) &&
                    <>
                        <Button
                            startIcon={<LeftIcon/>}
                            aria-label="Back"
                            onClick={() => setStep(1)}
                            >
                            Back to Entry
                        </Button>
                    </>
                }
            </DialogActions>
        </Dialog>
    )
}
